import { FolderCountResults } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder/FolderCountResults';
import { FolderForTree } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder/FolderForTree';
import { getFolderByPath } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder/FolderService';
import { FolderForTreeWrapper } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Portal';
import { PortalTabConfig } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Portal/PortalTabConfig';
import { ITreeNode } from 'components/Tree/CommonTree/CommonTreeNode';
import { getTreeStore, ITreeStore } from 'components/Tree/CommonTree/useTreeStore';
import { selectingFolderOnFavorites } from 'domains/portal/favorites/PortalFavoritesStore';
import {
  getFolderId,
  getFoldersPanelTitle,
  getRecentFoldersPanelTitle,
  getSideBarActiveTab,
  getSidebarContentSelector,
  getSystemFoldersPanelTitle,
  getShouldNotChangeTabFromFolderTree,
  PortalStore,
} from 'domains/portal/PortalStore';
import { QueueUtil } from 'utils/QueueUtils';
import { GetState, SetState } from 'types/zustand';
import { IFolderMessage, IFolderTreeNode } from './FolderTree';
import { addFolderNode, createFolderTreeStore, renameFolderNode, updateFolderCounter } from './FolderTreeUtils';
import {
  createTreeNodes,
  DefaultNodeLoader,
  getSubFolderForTree,
  getTreeNodesForFolderForTreeWrapper,
  IFolderNodeLoader,
  RecentNodeLoader,
  SystemNodeLoader,
} from './NodeLoaders';
import produce from 'immer';
import { useFolderActionsStore } from 'domains/portal/ActionsStore';
import { getShowSidebar, useSettingsStore } from 'domains/portal/SettingsStore';

export interface IFolderTreesStore {
  queue: QueueUtil;
  folderTreeStores: ReturnType<typeof createFolderTreesStores>;
  actions: ReturnType<typeof createFoldersStoreActions>;
}

export interface IFolderStoreCollection {
  getStore: () => ITreeStore<IFolderTreeNode> | undefined;
  loader: IFolderNodeLoader;
}

export function createFolderTreesStores() {
  return {
    folderTreeStores: new Map<string, IFolderStoreCollection>([]),
  };
}

export function createFoldersStoreActions(get: GetState<PortalStore>, set: SetState<PortalStore>) {
  const getNodeParentIds = (node: ITreeNode<IFolderTreeNode>): string[] => {
    // Confusing why, as it seems incorrect for a node to return itself as its parent.
    // but it seems necessary for selecting parent folder on deletion, currently DT-034835.
    const result = [node.nodeId];
    if (node.data!.parent) {
      result.push(...getNodeParentIds(node.data!.parent));
    }

    return result;
  };

  const getFolderStores = async (
    folderId: string,
    doNotChangeTab?: boolean
  ): Promise<[ITreeStore<IFolderTreeNode>[], FolderForTree | null]> => {
    if (!getShowSidebar(useSettingsStore.getState())) {
      return [[], null];
    }

    const folderForTree = (await getSubFolderForTree(folderId)) as FolderForTree;

    if (!folderForTree) {
      return [[], null];
    }

    const state = get();
    const { getTabForStore, getTreeStoresForFolder, isFolderVisibleForCurrentTab, setActiveSidebarTab } = state.actions;

    // We don't need to change tab after deleting workflow item
    doNotChangeTab = doNotChangeTab || getShouldNotChangeTabFromFolderTree(state);

    const result = getTreeStoresForFolder(folderForTree).filter(store => !!store);

    if (!doNotChangeTab && !isFolderVisibleForCurrentTab(folderForTree) && result.length) {
      setActiveSidebarTab(getTabForStore(result[0]!), folderId);
    }

    return [result as ITreeStore<IFolderTreeNode>[], folderForTree];
  };

  return {
    updateFolderCount: (countResult: FolderCountResults) => {
      const store = get();
      store.folderTreeStores.forEach(value => {
        value.getStore()?.enqueue(store => updateFolderCounter(store, countResult));
      });
    },
    addNode: async (folderMessage: IFolderMessage) => {
      const {
        actions: { enqueue },
      } = get();
      enqueue(async () => {
        const [stores, folderForTree] = await getFolderStores(folderMessage.folderId, true);
        for (let store of stores) {
          // eslint-disable-next-line no-await-in-loop
          await addFolderNode(folderMessage, folderForTree, () => getFolderId(get()), store);
        }
      });
    },
    renameNode: (folderId: string, folderName: string) => {
      const store = get();
      const folderStores = Array.from(store.folderTreeStores.values()).map(value => value.getStore());
      renameFolderNode(folderId, folderName, folderStores);
    },
    deleteNode: (folderId: string) => {
      const store = get();
      store.folderTreeStores.forEach(value => {
        value.getStore()?.enqueue(store => store.actions.deleteNode(folderId));
      });
    },
    refreshNode: (folderId: string) => {
      if (!getShowSidebar(useSettingsStore.getState())) {
        return; // this should be a NOOP if the folder tree isn't visible.
      }
      get().actions.enqueue(async () => {
        const store = get();
        const stores = Array.from(store.folderTreeStores.values());
        const folderForTree = await getSubFolderForTree(folderId);
        const promises = stores.map(async s => {
          const folderStore = s.getStore();
          return (
            folderStore &&
            folderStore.enqueue(store => store.actions.reloadNode(folderId, { folderNode: folderForTree }))
          );
        });

        await Promise.all(promises);
      });
    },
    loadAndSelectNode: async (folderId: string) => {
      get().actions.enqueue(async () => {
        const [storesContainingFolder, folder] = await getFolderStores(folderId);

        // could we just ge the promises and await them all like this:
        // await Promise.all(storesContainingFolder.map(store => store.actions.selectNode(folderId)))
        // do they need to be enqueued in order?
        for (let store of storesContainingFolder) {
          // eslint-disable-next-line no-await-in-loop
          await store.enqueue(async () => {
            await store.actions.selectNode(folderId);
          });
        }

        if (!folder) {
          return;
        }

        const storesNotContainingFolder = get().actions.getStoresNotMatched(storesContainingFolder);
        for (let store of storesNotContainingFolder) {
          // TODO do we need to unselect anymore, if we are just switching back and forth between tabs?
          // eslint-disable-next-line no-await-in-loop
          await store?.getStore()?.enqueue(store => store.actions.unselectNodes());
        }
      });
    },
    getParentFolderIds: (folderId: string): string[] => {
      const stores = get().folderTreeStores;
      const keys = Array.from(stores.keys()).filter(k => k !== get().recentFoldersPanelTitle);
      for (let key of keys) {
        const node = stores
          .get(key)
          ?.getStore()
          ?.actions.findNode(folderId);
        if (node) {
          return getNodeParentIds(node);
        }
      }
      return [];
    },
    hasNode: (folderId: string) => {
      if (!getShowSidebar(useSettingsStore.getState())) {
        return false; // nothing has a node if there's nothing there.
      }
      const state = get();
      let hasNode = false;
      const recentFolderTitle = getRecentFoldersPanelTitle(get());
      state.folderTreeStores.forEach((value, key) => {
        if (key === recentFolderTitle) {
          /* Recent folders should never be considered when checking against other folder trees because
           1. It doesn't really count as a concrete folder tree like Folders or System
           2. Its contents will always be re-fetched when switching to Recent tab,
              so we don't need to proactively update its contents
           3. Don't need to keep FVD up to date if it is only in in Recent. It will be re-fetched during navigation
           */
          return;
        }
        hasNode = hasNode || !!value.getStore()?.actions.findNode(folderId);
      });

      return hasNode;
    },
    findFolder: (folderId: string) => {
      const store = get();
      const stores = Array.from(store.folderTreeStores.values());
      for (let folderStore of stores) {
        const result = folderStore.getStore()?.actions.findNode(folderId);
        if (result) {
          return result;
        }
      }
    },
    enqueue: async (callback: (store: PortalStore) => Promise<void>) => {
      const store = get();
      return store.queue.enqueue({ callback: () => callback(store) });
    },
    getCurrentActiveFolderTree: () => {
      const activeTab = getSideBarActiveTab(get());
      return get().folderTreeStores.get(activeTab);
    },
    getTabForStore: (store: ITreeStore<IFolderTreeNode>) => {
      return store.treeId!;
    },

    // these getters are actually like _selectors_ with a 2nd argument, not actually actions.
    // we should refactor those so it's more clear, they only get data and have no side-effects.

    isFolderVisibleForCurrentTab: (folder: FolderForTree) => {
      // if the current tab is favorites, check their first.
      if (selectingFolderOnFavorites(get(), folder.FolderID)) {
        return true;
      }
      const treeStore = get().actions.getCurrentActiveFolderTree();

      return !!treeStore?.getStore()?.actions.findNode(folder.FolderID);
    },
    getTreeStoresForFolder(folder: FolderForTree) {
      const state = get();
      const result = [];
      const path = folder.FullPath.split('/');
      if (path[0] !== 'System') {
        const folderStore = state.folderTreeStores.get(state.foldersPanelTitle)?.getStore();
        folderStore && result.push(folderStore);
      } else {
        const systemStore = state.folderTreeStores.get(state.systemFoldersPanelTitle)?.getStore();
        systemStore && result.push(systemStore);
      }
      const treesContainingFolders = state.actions.getTreesContainingFolder(folder);

      for (let tree of treesContainingFolders) {
        if (result.filter(t => t?.treeId === tree?.treeId).length === 0) {
          result.push(tree);
        }
      }
      return result;
    },
    async getFolderByPath(folderPath: string) {
      return await getFolderByPath({ folderPath });
    },
    getTreesContainingFolder(folder: FolderForTree | string) {
      const folderId = typeof folder === 'object' ? folder.FolderID : folder;
      const result = [] as ITreeStore<IFolderTreeNode>[];
      get().folderTreeStores.forEach(store => {
        const folderStore = store.getStore();
        if (!!folderStore?.actions.findNode(folderId)) {
          result.push(folderStore);
        }
      });
      return result.filter(f => !!f);
    },
    getStoresNotMatched(stores: ITreeStore<IFolderTreeNode>[]) {
      const state = get();
      const treeStoresKeys = Array.from(get().folderTreeStores.keys()).filter(key =>
        stores.every(store => store.treeId !== key)
      );

      return treeStoresKeys.map(key => state.folderTreeStores.get(key)).filter(s => !!s);
    },
    createFoldersStore(tabs: PortalTabConfig[], currentFolderId: string) {
      const state = get();
      const folders: FolderForTreeWrapper[] | undefined = getFoldersForTabConfig(tabs, getFoldersPanelTitle(get()));
      const nodes = folders && getTreeNodesForFolderForTreeWrapper(folders, currentFolderId);

      // initialize connector for folder change
      const { findFolder, hasNode, renameNode, addNode, deleteNode, refreshNode, updateFolderCount } = state.actions;
      useFolderActionsStore.getState().actions.setTreeShim({
        findFolder,
        getCurrentFolderId: () => getFolderId(get()),
        // TODO this is how it was, but won't this only evaluate the Folders tab? Won't we need system, and other project views here, eventually?
        getRootNodes: () =>
          get()
            .folderTreeStores.get(get().foldersPanelTitle)
            ?.getStore()?.nodes ?? [],
        hasNode,
        renameNode,
        addNode,
        deleteNode,
        updateFolderCount,
        refreshNode,
      });

      return state.actions.createFolderTreeStore(state.foldersPanelTitle, new DefaultNodeLoader(), nodes);
    },
    createRecentFoldersStore(tabs: PortalTabConfig[], currentFolderId: string) {
      const state = get();
      const folders: FolderForTree[] | undefined = getFoldersForTabConfig(tabs, getRecentFoldersPanelTitle(get()));
      const nodes = folders && createTreeNodes(folders, currentFolderId, true);
      return state.actions.createFolderTreeStore(state.recentFoldersPanelTitle, new RecentNodeLoader(), nodes);
    },
    createSystemFoldersStore(tabs: PortalTabConfig[], currentFolderId: string) {
      const state = get();
      const folders: FolderForTreeWrapper[] | undefined = getFoldersForTabConfig(
        tabs,
        getSystemFoldersPanelTitle(get())
      );
      const nodes = folders && getTreeNodesForFolderForTreeWrapper(folders, currentFolderId);
      return state.actions.createFolderTreeStore(state.systemFoldersPanelTitle, new SystemNodeLoader(), nodes);
    },
    registerFolderStore(treeId: string, loader: IFolderNodeLoader) {
      set(
        produce<PortalStore>(state => {
          state.folderTreeStores.set(treeId, {
            loader,
            getStore: () => getTreeStore(treeId),
          } as IFolderStoreCollection);
        })
      );
    },
    async createFolderTreeStore(treeId: string, loader: IFolderNodeLoader, nodes: ITreeNode<IFolderTreeNode>[] = []) {
      return createFolderTreeStore(
        treeId,
        loader,
        nodes,
        () => getSidebarContentSelector(get()),
        () => getFolderId(get()),
        get().actions.registerFolderStore
      );
    },
  };
}

export function getFolderTreeIdForTab(state: PortalStore, tab?: string) {
  tab = tab || getSideBarActiveTab(state);

  return tab;
}

type ITabNameOrId =
  | {
      tabName: string;
      tabId?: never;
    }
  | {
      tabName?: never;
      tabId: string;
    };

export function getFolderTreeStoreForTab(state: PortalStore, { tabName, tabId }: ITabNameOrId) {
  if (!tabName && !tabId) {
    return;
  }

  const tab = tabId || getFolderTreeIdForTab(state, tabName);

  return state.folderTreeStores.get(tab)?.getStore();
}

export function getNodesForTab(state: PortalStore, tabNameOrId: ITabNameOrId) {
  const treeStore = getFolderTreeStoreForTab(state, tabNameOrId);
  return treeStore?.nodes ?? [];
}

function getFoldersForTabConfig<T>(tabs: PortalTabConfig[], tabTitle: string): T | undefined {
  return tabs.find(tab => tab.Folders.Name === tabTitle)?.Folders?.OutputValue as T;
}
